/* eslint-disable import/order */
// !!!NOTE: Disabling the importing order rule as we want the settings to be imported before any other imports. Maybe fix this in the future.

import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { locale } from "dayjs";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import TagManager from "react-gtm-module";
import { HelmetProvider } from "react-helmet-async";
import { RouterProvider } from "react-router-dom";
import "dayjs/locale/fi";
import "dayjs/locale/sv";

// !! IMOPORTANT  !! These needs to be initialized before any @krea/customer-features import //
import "./i18n";

import HttpService from "@krea/customer-features/services/httpService";
import { queryClient } from "@krea/customer-features/store/queryClient";
import { config } from "@krea/shared/config";
import { CONFIG_CATALOG } from "@krea/shared/config/catalog";
import {
  initSentryOptions,
  SENTRY_REPLAY_PRIVACY_DEFAULTS,
} from "@krea/shared/utils/initSentry";

import { router } from "./router";
import settings from "./settings";

locale("sv");

const isDeployedEnvironment = config.isSentryEnabled;

const sentryOptions: Sentry.BrowserOptions = {
  ...initSentryOptions(config, { filterBrowserNoise: true }),
  dsn: "https://a9a86248d74b4c7881dec3b30c3ba74a@o242905.ingest.us.sentry.io/5307143",
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      ...SENTRY_REPLAY_PRIVACY_DEFAULTS,
      networkDetailAllowUrls: [`${CONFIG_CATALOG.production.apiRootUrl}/api/`],
    }),
  ],
};

// eslint-disable-next-line react-refresh/only-export-components
function Root() {
  useEffect(() => {
    const instance = HttpService.getInstance();
    instance.setupInterceptors();

    const tagManagerArgs = {
      gtmId: settings.gtmId,
      dataLayer: {
        event: "initTracking",
        environment: settings.environment,
      },
    };

    TagManager.initialize(tagManagerArgs);

    if (isDeployedEnvironment) {
      Sentry.init(sentryOptions);
    }
  }, []);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          {!isDeployedEnvironment && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
          <RouterProvider router={router} />
        </HelmetProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

createRoot(document.getElementById("root")!).render(<Root />);
